import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Check, Star, Zap, Crown, ArrowLeft, Eye, Store } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [businesses, setBusinesses] = useState([]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscription();
      fetchBusinesses();
    }
  }, [isAuthenticated, token]);

  const fetchSubscription = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/my-subscription`, {
        headers,
        withCredentials: true
      });
      setSubscription(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch subscription:', error);
      }
    }
  };

  const fetchBusinesses = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/my-businesses`, {
        headers,
        withCredentials: true
      });
      setBusinesses(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch businesses:', error);
      }
    }
  };

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to subscribe');
      navigate('/login');
      return;
    }

    if (planId.includes('free')) {
      toast.info('You are already on the free plan');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/payments/checkout`,
        {
          plan_id: planId,
          payment_type: 'subscription',
          origin_url: window.location.origin
        },
        { headers, withCredentials: true }
      );
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureBusiness = async (businessId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in first');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/payments/checkout`,
        {
          business_id: businessId,
          payment_type: 'featured',
          origin_url: window.location.origin
        },
        { headers, withCredentials: true }
      );
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  // Browser Plans
  const browserPlans = [
    {
      id: 'browser_free',
      name: 'Free',
      price: 0,
      description: 'Basic browsing access',
      icon: Eye,
      features: [
        'View 5 businesses per day',
        'Basic search',
        'View business details'
      ],
      buttonText: 'Current Plan',
      popular: false
    },
    {
      id: 'browser_pro',
      name: 'Browser Pro',
      price: 9.99,
      description: 'Unlimited discovery',
      icon: Zap,
      features: [
        'Unlimited browsing',
        'Advanced filters',
        'Save favorites',
        'Contact businesses directly',
        'Early access to new listings'
      ],
      buttonText: 'Upgrade to Pro',
      popular: true
    }
  ];

  // Owner Plans
  const ownerPlans = [
    {
      id: 'owner_basic',
      name: 'Basic',
      price: 19.99,
      description: 'Get listed',
      icon: Store,
      features: [
        '1 business listing',
        'Basic profile',
        'Contact info display',
        'Customer reviews'
      ],
      buttonText: 'Start Listing',
      popular: false
    },
    {
      id: 'owner_pro',
      name: 'Pro',
      price: 49.99,
      description: 'Grow your presence',
      icon: Zap,
      features: [
        'Up to 5 listings',
        'Featured badge',
        'Analytics dashboard',
        'Priority support',
        'Social media links'
      ],
      buttonText: 'Go Pro',
      popular: true
    },
    {
      id: 'owner_premium',
      name: 'Premium',
      price: 99.99,
      description: 'Maximum exposure',
      icon: Crown,
      features: [
        'Unlimited listings',
        'Top search placement',
        'Verified badge',
        'Social media promotion',
        'Dedicated support',
        'Custom branding'
      ],
      buttonText: 'Go Premium',
      popular: false
    }
  ];

  const renderPlanCard = (plan, currentPlan) => {
    const Icon = plan.icon;
    const isCurrent = currentPlan === plan.id;
    
    return (
      <Card
        key={plan.id}
        className={`relative ${plan.popular ? 'border-emerald-deep border-2 shadow-lg' : 'border-stone-200'}`}
        data-testid={`plan-${plan.id}`}
      >
        {plan.popular && (
          <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-deep text-white">
            Most Popular
          </Badge>
        )}
        
        <CardHeader className="text-center pb-2">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
            plan.popular ? 'bg-emerald-deep' : 'bg-stone-100'
          }`}>
            <Icon className={`w-6 h-6 ${plan.popular ? 'text-white' : 'text-emerald-deep'}`} />
          </div>
          <CardTitle className="font-display text-2xl">{plan.name}</CardTitle>
          <CardDescription>{plan.description}</CardDescription>
        </CardHeader>
        
        <CardContent className="text-center">
          <div className="mb-6">
            <span className="font-display text-5xl font-bold text-stone-900">
              ${plan.price}
            </span>
            {plan.price > 0 && <span className="text-stone-500">/month</span>}
          </div>
          
          <ul className="space-y-3 text-left">
            {plan.features.map((feature, idx) => (
              <li key={`${plan.id}-feature-${idx}`} className="flex items-center gap-2 text-stone-600">
                <Check className="w-5 h-5 text-emerald-deep flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>
        
        <CardFooter>
          <Button
            onClick={() => handleSubscribe(plan.id)}
            disabled={loading || isCurrent || plan.price === 0}
            className={`w-full h-12 rounded-full ${
              plan.popular
                ? 'bg-emerald-deep hover:bg-emerald-deep/90 text-white'
                : 'bg-stone-100 hover:bg-stone-200 text-stone-700'
            }`}
            data-testid={`subscribe-${plan.id}`}
          >
            {isCurrent ? 'Current Plan' : plan.buttonText}
          </Button>
        </CardFooter>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-stone-white" data-testid="pricing-page">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-deep mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-900 mb-4">
              Choose Your Plan
            </h1>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Support Black-owned businesses or list your own. Choose the plan that fits your needs.
            </p>
          </div>

          {/* Disclaimer for Business Owners */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-8 max-w-3xl mx-auto">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-amber-800 mb-1">Black-Owned Businesses Only</h3>
                <p className="text-sm text-amber-700">
                  Uprize Black Biz is dedicated exclusively to supporting and promoting <strong>Black-owned businesses</strong>. 
                  Business listing subscriptions are only available for Black-owned establishments that are legally registered.
                </p>
                <p className="text-sm text-amber-700 mt-2">
                  <strong>Requirements:</strong> All businesses must be legally registered with their state and maintain active status. 
                  Food trucks and restaurants must have current, valid permits and all required licenses. Businesses serving alcohol 
                  must maintain proper, up-to-date alcohol permits and licenses.
                </p>
                <p className="text-sm text-amber-700 mt-2">
                  <strong>Policy:</strong> If a business is found to not be Black-owned, not legally registered, or lacking proper 
                  permits/licenses, the listing will be deactivated and all purchases will be refunded. You may re-register 
                  once all requirements are met. We prioritize customer satisfaction and safety.
                </p>
              </div>
            </div>
          </div>

          {/* Current Status */}
          {subscription && (
            <div className="bg-white rounded-xl p-6 border border-stone-200 mb-8">
              <h3 className="font-display text-lg font-semibold text-stone-900 mb-4">Your Current Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-sm text-stone-500 mb-1">Browser Subscription</p>
                  <p className="font-semibold text-stone-900">
                    {subscription.browser_plan?.name || 'Free'}
                  </p>
                  {subscription.remaining_daily_views >= 0 && (
                    <p className="text-sm text-emerald-deep mt-1">
                      {subscription.remaining_daily_views} views remaining today
                    </p>
                  )}
                </div>
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-sm text-stone-500 mb-1">Business Listing</p>
                  <p className="font-semibold text-stone-900">
                    {subscription.owner_plan?.name || 'No Plan'}
                  </p>
                  {subscription.owner_subscription && (
                    <p className="text-sm text-emerald-deep mt-1">
                      {subscription.listing_count} / {subscription.listing_limit === 999 ? '∞' : subscription.listing_limit} listings used
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Subscription Plans Tabs */}
          <Tabs defaultValue="browser" className="mb-16">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
              <TabsTrigger value="browser" className="gap-2">
                <Eye className="w-4 h-4" />
                For Browsers
              </TabsTrigger>
              <TabsTrigger value="owner" className="gap-2">
                <Store className="w-4 h-4" />
                For Business Owners
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="browser">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
                {browserPlans.map((plan) => 
                  renderPlanCard(plan, subscription?.browser_subscription)
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="owner">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {ownerPlans.map((plan) => 
                  renderPlanCard(plan, subscription?.owner_subscription)
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Featured Listing Section */}
          {isAuthenticated && businesses.length > 0 && (
            <div className="bg-white rounded-2xl p-8 border border-stone-200">
              <div className="text-center mb-8">
                <h2 className="font-display text-3xl font-bold text-stone-900 mb-2">
                  Feature Your Business
                </h2>
                <p className="text-stone-600">
                  One-time payment of <span className="font-bold text-legacy-gold">$29</span> to get the Featured badge and top placement
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {businesses.map((business) => (
                  <div
                    key={business.business_id}
                    className="p-4 border border-stone-200 rounded-xl flex items-center justify-between"
                  >
                    <div>
                      <h3 className="font-semibold text-stone-900">{business.name}</h3>
                      <p className="text-sm text-stone-500">{business.category}</p>
                    </div>
                    {business.is_featured ? (
                      <Badge className="bg-legacy-gold text-white">Featured</Badge>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleFeatureBusiness(business.business_id)}
                        disabled={loading}
                        className="bg-legacy-gold hover:bg-legacy-gold/90 text-white"
                        data-testid={`feature-${business.business_id}`}
                      >
                        Feature $29
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Not signed in message */}
          {!isAuthenticated && (
            <div className="text-center bg-white rounded-2xl p-8 border border-stone-200">
              <p className="text-stone-600 mb-4">
                Sign in to subscribe and start browsing or listing your business
              </p>
              <Link to="/login">
                <Button className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full">
                  Sign In
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PricingPage;
